import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import { techBadges } from "../data/mock";
import { trackContactUsClick } from "../lib/analytics";

const techBadgeRoutes = {
  "AWS": "/tech/aws",
  "Azure": "/tech/azure",
  "GCP": "/tech/gcp",
  "Kubernetes": "/tech/kubernetes",
  "Terraform": "/tech/terraform",
  "AIOps": "/tech/aiops"
};

export default function Hero() {
  return (
    <section
      id="home"
      className="relative overflow-hidden nst-navy text-white pt-[120px] pb-24 lg:pb-32"
    >
      {/* decorative grid */}
      <div className="absolute inset-0 bg-grid-dark opacity-40" />
      {/* glow orbs */}
      <div className="glow-orb w-[420px] h-[420px] -top-20 -left-20" style={{ background: "#2E6BF6" }} />
      <div className="glow-orb w-[520px] h-[520px] top-20 -right-32" style={{ background: "#16C47F" }} />

      {/* hero image at right */}
      <div
        className="absolute right-0 top-0 bottom-0 w-1/2 opacity-40 lg:opacity-50 hidden md:block"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1737644467636-6b0053476bb2')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage: "linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)",
          WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="pill-label on-dark">
            <Sparkles className="w-3.5 h-3.5" />
            Principal-Level Cloud Engineering
          </span>

          <h1 className="mt-8 text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.02]">
            Scale Cloud Infrastructure <br />
            <span className="text-white">Without the</span>{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Growing Pains</span>
              <span className="absolute -bottom-1 left-0 right-0 h-[10px] bg-[#2E6BF6]/80 rounded-full z-0" />
            </span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
            Enterprise-grade cloud platforms designed by principal engineers who've built systems at scale. We architect, implement, and operate your multi-cloud infrastructure.
          </p>

          <div className="mt-8 flex flex-wrap gap-2.5">
            {techBadges.map((t) => (
              <Link
                key={t}
                to={techBadgeRoutes[t]}
                className="tech-badge hover:scale-105 hover:shadow-md transition-all duration-200 cursor-pointer"
              >
                {t}
              </Link>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a 
              href="#contact" 
              onClick={() => trackContactUsClick('hero')}
              className="btn-navy" 
              style={{ background: "#2E6BF6" }}
            >
              Talk to a Cloud Architect <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/assessment"
              data-testid="hero-assessment-cta"
              className="btn-ghost-light"
            >
              Free 7-day Audit <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Floating stats card */}
        <div className="mt-16 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-4xl">
          {[
            { k: "99.99%", v: "Platform Uptime" },
            { k: "40+", v: "Cloud Projects" },
            { k: "Multi-Cloud", v: "AWS / Azure / GCP" },
            { k: "24/7", v: "Engineering Support" },
          ].map((s) => (
            <div key={s.v} className="border-t border-white/15 pt-4">
              <div className="text-2xl md:text-3xl font-extrabold tracking-tight">{s.k}</div>
              <div className="text-xs md:text-sm text-white/60 mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
